import {
  DownOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Button,
  InputNumber,
  Modal,
  Select,
  Spin,
  Tabs,
  Tooltip,
  message,
} from "antd";
import {
  ConceptDropSelect,
  StockLink,
  StockPrice,
  ratioToStockPricePct,
  type StockNotesTabProps,
  type StockSearchBrief,
} from "fin-web-components";
import { KChart, type KLineData } from "kchart";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchStockWithCatalog } from "../../components/fin-web/SearchStockWithCatalog";
import type { StockLinkDetailState } from "../../components/fin-web/useStockLinkDetail";
import { useStockLinkDetail } from "../../components/fin-web/useStockLinkDetail";
import { globalModel } from "../../global/GlobalModel";
import { TacticsPageUiConfig, tacticsPageCssVars, tacticsWidthCss } from "./Config";
import {
  buildTacticsKlineData,
  findTacticsAssessForKlineTime,
} from "./buildTacticsKlineData";
import { TacticsApi } from "./TacticsApi";
import type {
  TacticsDailyScorePointDTO,
  TacticsSelectableConceptDTO,
  TacticsSignalIconOptionDTO,
  TacticsStockKlineBarDTO,
  TacticsStockScoreRowDTO,
  TacticsStrategyBriefDTO,
  TacticsStrategyScoreType,
  TacticsUserSignalSettingsDTO,
} from "./TacticsTypes";
import "./TacticsPage.css";

dayjs.extend(customParseFormat);

const RECT_BTN = "tactics-rect-modal-btn";

function tacticsApiDateTimeString(v: unknown): string {
  if (v == null || v === "") {
    return "";
  }
  if (typeof v === "string") {
    return v.trim();
  }
  if (typeof v === "number" && Number.isFinite(v)) {
    return dayjs(v).format("YYYY-MM-DD HH:mm:ss");
  }
  if (v instanceof Date) {
    return dayjs(v).format("YYYY-MM-DD HH:mm:ss");
  }
  return String(v).trim();
}

function formatDataDateLabel(raw: unknown): string {
  const s = tacticsApiDateTimeString(raw);
  if (!s) return "—";
  const d = dayjs(s, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD"], true);
  if (!d.isValid()) return s;
  return d.format("M月D日");
}

function stockRowKey(r: TacticsStockScoreRowDTO): string {
  return r.stockCode?.trim() || "";
}

function stockRowDisplayName(r: TacticsStockScoreRowDTO): string {
  const code = stockRowKey(r);
  return r.stockName?.trim() || code;
}

function normalizeStrategyScoreType(raw: string | undefined): TacticsStrategyScoreType {
  const v = raw?.trim().toUpperCase();
  if (v === "RISK") {
    return "RISK";
  }
  if (v === "ALL") {
    return "ALL";
  }
  return "CHANCE";
}

function formatTacticsScoreValue(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) {
    return "—";
  }
  return v.toFixed(1);
}

function buildKlineScoreHint(
  assess: TacticsDailyScorePointDTO | null,
  strategyType: TacticsStrategyScoreType
): string {
  if (!assess) {
    return "该日暂无分数";
  }
  const opp = formatTacticsScoreValue(assess.score);
  const risk = formatTacticsScoreValue(assess.riskScore);
  if (strategyType === "CHANCE") {
    return `机会 ${opp}`;
  }
  if (strategyType === "RISK") {
    return `风险 ${risk}`;
  }
  return `机会 ${opp} / 风险 ${risk}`;
}

export interface TacticsPageProps {
  spaceId: string;
  spaceProductId: string;
}

const TacticsPage: React.FC<TacticsPageProps> = ({ spaceId, spaceProductId }) => {
  const [strategyLoading, setStrategyLoading] = useState(true);
  const [dropdownItems, setDropdownItems] = useState<TacticsStrategyBriefDTO[]>([]);
  const [strategyId, setStrategyId] = useState<string>("");
  const strategyInitRef = useRef(false);

  useEffect(() => {
    strategyInitRef.current = false;
  }, [spaceProductId]);
  const [marketStrategies, setMarketStrategies] = useState<TacticsStrategyBriefDTO[]>(
    []
  );

  const [marketOpen, setMarketOpen] = useState(false);
  const [marketPick, setMarketPick] = useState<string>("");
  const [marketHover, setMarketHover] = useState<string>("");

  const [leftTab, setLeftTab] = useState<
    "top" | "watchlist" | "holdings" | "concept"
  >("top");
  const [listLoading, setListLoading] = useState(false);
  const [topPayload, setTopPayload] = useState<{
    dataDate: string;
    rows: TacticsStockScoreRowDTO[];
  }>({ dataDate: "", rows: [] });
  const [watchRows, setWatchRows] = useState<TacticsStockScoreRowDTO[]>([]);
  const [holdRows, setHoldRows] = useState<TacticsStockScoreRowDTO[]>([]);
  const [conceptRows, setConceptRows] = useState<TacticsStockScoreRowDTO[]>([]);
  const [conceptConceptDtos, setConceptConceptDtos] = useState<
    TacticsSelectableConceptDTO[]
  >([]);
  const [tactikConceptCode, setTactikConceptCode] = useState<string>("");

  /** 左侧列表选中（与搜索框无关） */
  const [listSelectedStockCode, setListSelectedStockCode] = useState<string>("");
  /** 左上搜索框选中（与左侧列表无关） */
  const [searchStockCode, setSearchStockCode] = useState<string>("");
  /** 右侧图表/详情展示的股票 */
  const [panelStockCode, setPanelStockCode] = useState<string>("");
  const [pickedFromSearch, setPickedFromSearch] = useState(false);
  const [selectedKlineTime, setSelectedKlineTime] = useState<number | null>(null);

  useEffect(() => {
    setPickedFromSearch(false);
  }, [leftTab, strategyId]);

  useEffect(() => {
    setSelectedKlineTime(null);
  }, [panelStockCode, strategyId]);

  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);
  const [panelLoading, setPanelLoading] = useState(false);
  const [klineBars, setKlineBars] = useState<TacticsStockKlineBarDTO[]>([]);
  const [assessPoints, setAssessPoints] = useState<TacticsDailyScorePointDTO[]>([]);

  const [signalSettingsOpen, setSignalSettingsOpen] = useState(false);
  const [signalBusy, setSignalBusy] = useState(false);
  const [signalOpportunityOpts, setSignalOpportunityOpts] = useState<
    TacticsSignalIconOptionDTO[]
  >([]);
  const [signalRiskOpts, setSignalRiskOpts] = useState<TacticsSignalIconOptionDTO[]>(
    []
  );
  const [signalSaved, setSignalSaved] =
    useState<TacticsUserSignalSettingsDTO | null>(null);
  const [draftOppThr, setDraftOppThr] = useState<number | null>(70);
  const [draftRiskThr, setDraftRiskThr] = useState<number | null>(30);
  const [draftOppIcon, setDraftOppIcon] = useState<string | null>(null);
  const [draftRiskIcon, setDraftRiskIcon] = useState<string | null>(null);

  const { stockLinkDetail, requestStockDetailForLink, buildStockNotesTabProps } =
    useStockLinkDetail(spaceId);
  const requestStockDetailRef = useRef(requestStockDetailForLink);
  requestStockDetailRef.current = requestStockDetailForLink;

  const leftTabsIndicator = useMemo(
    () => ({ size: (origin: number) => origin, align: "center" as const }),
    []
  );

  useEffect(() => {
    const el = chartRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(([entry]) => {
      setChartWidth(entry.contentRect.width);
      setChartHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let alive = true;
    setStrategyLoading(true);
    void (async () => {
      const res = await TacticsApi.listDropdownStrategies(spaceId, spaceProductId);
      if (!alive) return;
      const rows = Array.isArray(res.items) ? res.items : [];
      setDropdownItems(rows);
      if (!strategyInitRef.current && rows.length) {
        let pick = rows[0].id.trim();
        try {
          const def = await TacticsApi.resolveDefaultStrategy(spaceId, spaceProductId);
          if (def && rows.some((r) => r.id.trim() === def)) {
            pick = def;
          }
        } catch {
          /* 使用下拉首项 */
        }
        setStrategyId(pick);
        strategyInitRef.current = true;
        void TacticsApi.recordStrategyVisit(spaceId, spaceProductId, pick);
      }
      setStrategyLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [spaceId, spaceProductId]);

  useEffect(() => {
    let alive = true;
    void TacticsApi.listSelectableConcepts(spaceId, spaceProductId).then((rows) => {
      if (!alive) return;
      setConceptConceptDtos(rows);
    });
    return () => {
      alive = false;
    };
  }, [spaceId, spaceProductId]);

  useEffect(() => {
    if (!strategyId.trim()) return undefined;
    let alive = true;
    setListLoading(true);
    const sid = strategyId.trim();
    let p: Promise<unknown>;

    if (leftTab === "top") {
      p = TacticsApi.listTopScores(spaceId, spaceProductId, sid).then((r) => {
        if (!alive) return;
        setTopPayload({
          dataDate: tacticsApiDateTimeString(r.dataDate),
          rows: Array.isArray(r.rows) ? r.rows : [],
        });
      });
    } else if (leftTab === "watchlist") {
      p = TacticsApi.listWatchlistScores(spaceId, spaceProductId, sid).then((rows) => {
        if (!alive) return;
        setWatchRows(rows);
      });
    } else if (leftTab === "holdings") {
      p = TacticsApi.listHoldingsScores(spaceId, spaceProductId, sid).then((rows) => {
        if (!alive) return;
        setHoldRows(rows);
      });
    } else if (leftTab === "concept" && tactikConceptCode.trim()) {
      p = TacticsApi.listConceptStockScores(
        spaceId,
        spaceProductId,
        sid,
        tactikConceptCode.trim()
      ).then((rows) => {
        if (!alive) return;
        setConceptRows(rows);
      });
    } else {
      setListLoading(false);
      return () => {
        alive = false;
      };
    }

    void p.finally(() => {
      if (alive) setListLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [tactikConceptCode, leftTab, spaceId, spaceProductId, strategyId]);

  const displayRows = useMemo(() => {
    if (leftTab === "top") return topPayload.rows;
    if (leftTab === "watchlist") return watchRows;
    if (leftTab === "holdings") return holdRows;
    if (leftTab === "concept") return conceptRows;
    return [];
  }, [conceptRows, holdRows, leftTab, topPayload.rows, watchRows]);

  useEffect(() => {
    if (pickedFromSearch) {
      return;
    }
    if (!displayRows.length) {
      setListSelectedStockCode("");
      setPanelStockCode("");
      return;
    }
    const codes = displayRows.map(stockRowKey);
    let nextCode: string | null = null;
    setListSelectedStockCode((prev) => {
      const current = prev.trim();
      if (current && codes.includes(current)) {
        nextCode = current;
        return prev;
      }
      nextCode = codes[0] ?? "";
      return nextCode;
    });
    setPanelStockCode(nextCode ?? "");
    if (nextCode) {
      requestStockDetailRef.current(nextCode);
    }
  }, [displayRows, pickedFromSearch]);

  const loadChartPanel = useCallback(
    async (code: string) => {
      const c = code.trim();
      const sid = strategyId.trim();
      if (!c || !sid) {
        setKlineBars([]);
        setAssessPoints([]);
        return;
      }
      setPanelLoading(true);
      try {
        const [klines, series] = await Promise.all([
          TacticsApi.listStockDayKlines(spaceId, spaceProductId, c, 300),
          TacticsApi.listDailyStockScores(spaceId, spaceProductId, sid, c),
        ]);
        setKlineBars(klines);
        setAssessPoints(series);
      } catch (e) {
        message.error(e instanceof Error ? e.message : "加载图表失败");
        setKlineBars([]);
        setAssessPoints([]);
      } finally {
        setPanelLoading(false);
      }
    },
    [spaceId, spaceProductId, strategyId]
  );

  useEffect(() => {
    const code = panelStockCode.trim();
    if (!code || !strategyId.trim()) {
      setKlineBars([]);
      setAssessPoints([]);
      return;
    }
    void loadChartPanel(code);
  }, [loadChartPanel, panelStockCode, strategyId]);

  useEffect(() => {
    let alive = true;
    void TacticsApi.getUserSignalSettings(spaceId, spaceProductId).then((row) => {
      if (!alive) return;
      setSignalSaved(row);
    });
    return () => {
      alive = false;
    };
  }, [spaceId, spaceProductId]);

  async function loadMarketRows(): Promise<void> {
    const rows = await TacticsApi.listMarketStrategies(spaceId, spaceProductId);
    setMarketStrategies(rows);
    setMarketPick((rows.find((x) => x.id === strategyId) ?? rows[0])?.id ?? "");
  }

  const openSignalSettings = (): void => {
    setSignalSettingsOpen(true);
    setSignalBusy(true);
    void (async () => {
      const opts = await TacticsApi.listSignalIconOptions();
      setSignalOpportunityOpts(opts.opportunity);
      setSignalRiskOpts(opts.risk);
      const saved = await TacticsApi.getUserSignalSettings(spaceId, spaceProductId);
      if (saved) {
        setDraftOppThr(
          typeof saved.opportunityThreshold === "number"
            ? saved.opportunityThreshold
            : null
        );
        setDraftRiskThr(
          typeof saved.riskThreshold === "number" ? saved.riskThreshold : null
        );
        setDraftOppIcon(saved.opportunityIconUrl ?? opts.opportunity[0]?.imageUrl ?? null);
        setDraftRiskIcon(saved.riskIconUrl ?? opts.risk[0]?.imageUrl ?? null);
      } else {
        setDraftOppIcon(opts.opportunity[0]?.imageUrl ?? null);
        setDraftRiskIcon(opts.risk[0]?.imageUrl ?? null);
      }
      setSignalBusy(false);
    })();
  };

  const strategySelectOptions = useMemo(
    () =>
      dropdownItems.map((s) => ({
        value: s.id,
        label: s.name.trim() || s.id,
      })),
    [dropdownItems]
  );

  const selectedStrategyType = useMemo((): TacticsStrategyScoreType => {
    const hit =
      dropdownItems.find((s) => s.id.trim() === strategyId.trim()) ??
      marketStrategies.find((s) => s.id.trim() === strategyId.trim()) ??
      null;
    return normalizeStrategyScoreType(hit?.strategyType);
  }, [dropdownItems, marketStrategies, strategyId]);

  const selectedKlineAssess = useMemo(() => {
    if (selectedKlineTime == null) {
      return null;
    }
    return findTacticsAssessForKlineTime(assessPoints, selectedKlineTime);
  }, [assessPoints, selectedKlineTime]);

  const klineScoreHintText = useMemo(() => {
    if (selectedKlineTime == null) {
      return "点击/选择 K 线查看分数";
    }
    return buildKlineScoreHint(selectedKlineAssess, selectedStrategyType);
  }, [selectedKlineAssess, selectedKlineTime, selectedStrategyType]);

  const onKlineSelect = useCallback((kline: KLineData) => {
    setSelectedKlineTime(kline.time);
  }, []);

  const onKlineDeselect = useCallback(() => {
    setSelectedKlineTime(null);
  }, []);

  const selectedStock = useMemo(
    () =>
      displayRows.find((r) => stockRowKey(r) === panelStockCode.trim()) ??
      null,
    [displayRows, panelStockCode]
  );

  const selectedStockName = useMemo(() => {
    const code = panelStockCode.trim();
    if (!code) {
      return "";
    }
    if (selectedStock?.stockName?.trim()) {
      return selectedStock.stockName.trim();
    }
    if (stockLinkDetail.code === code && stockLinkDetail.data?.stockName?.trim()) {
      return stockLinkDetail.data.stockName.trim();
    }
    const hit = globalModel.basicStocksCatalog.find((s) => s.code.trim() === code);
    return hit?.name?.trim() || code;
  }, [selectedStock, panelStockCode, stockLinkDetail.code, stockLinkDetail.data]);

  const selectedStockPrice = useMemo(() => {
    if (selectedStock) {
      return {
        price: selectedStock.lastPrice,
        change: ratioToStockPricePct(selectedStock.pctChange),
      };
    }
    const code = panelStockCode.trim();
    if (stockLinkDetail.code === code && stockLinkDetail.data) {
      return {
        price: stockLinkDetail.data.latestPrice,
        change: ratioToStockPricePct(stockLinkDetail.data.priceChange),
      };
    }
    return null;
  }, [selectedStock, panelStockCode, stockLinkDetail.code, stockLinkDetail.data]);

  const klineData = useMemo(
    () => buildTacticsKlineData(klineBars, assessPoints, signalSaved),
    [assessPoints, klineBars, signalSaved]
  );

  const chartKey = `${panelStockCode}-${strategyId}-${signalSaved?.opportunityIconUrl ?? ""}-${signalSaved?.riskIconUrl ?? ""}-${signalSaved?.opportunityThreshold ?? ""}-${signalSaved?.riskThreshold ?? ""}`;

  const onPickRow = useCallback(
    (r: TacticsStockScoreRowDTO) => {
      const code = stockRowKey(r);
      setPickedFromSearch(false);
      setListSelectedStockCode(code);
      setPanelStockCode(code);
      requestStockDetailForLink(code);
    },
    [requestStockDetailForLink]
  );

  const onSearchStockSelect = useCallback(
    (stock: StockSearchBrief) => {
      const code = stock.code.trim();
      setPickedFromSearch(true);
      setSearchStockCode(code);
      setPanelStockCode(code);
      requestStockDetailForLink(code);
    },
    [requestStockDetailForLink]
  );

  const confirmMarketPick = (): void => {
    const pick = marketPick.trim();
    if (!pick) {
      message.warning("请选择一个策略");
      return;
    }
    setPickedFromSearch(false);
    setStrategyId(pick);
    setMarketOpen(false);
    message.success("已切换策略");
  };

  const persistSignalSettings = async (): Promise<void> => {
    const body: TacticsUserSignalSettingsDTO = {
      opportunityThreshold: draftOppThr ?? null,
      opportunityIconUrl: draftOppIcon,
      riskThreshold: draftRiskThr ?? null,
      riskIconUrl: draftRiskIcon,
    };
    await TacticsApi.saveUserSignalSettings(spaceId, spaceProductId, body);
    setSignalSaved(body);
    setSignalSettingsOpen(false);
    message.success("已保存");
  };

  function renderStockRows(rows: TacticsStockScoreRowDTO[]): JSX.Element {
    return (
      <div className="tactics-scroll-list">
        {rows.map((r) =>
          tacticStockRowJsx({
            row: r,
            active: !!listSelectedStockCode && stockRowKey(r) === listSelectedStockCode,
            stockLinkDetail,
            onPick: () => onPickRow(r),
            onRequestDetail: requestStockDetailForLink,
            buildStockNotesTabProps,
          })
        )}
      </div>
    );
  }

  const leftTabsItems = [
    {
      key: "top",
      label: "高分",
      children: (
        <div className="tactics-tab-pane-inner">
          {topPayload.dataDate ? (
            <div className="tactics-muted-note">
              数据日期：{formatDataDateLabel(topPayload.dataDate)}
            </div>
          ) : null}
          {renderStockRows(displayRows)}
        </div>
      ),
    },
    {
      key: "watchlist",
      label: "自选",
      children: (
        <div className="tactics-tab-pane-inner">{renderStockRows(displayRows)}</div>
      ),
    },
    {
      key: "holdings",
      label: "持仓",
      children: (
        <div className="tactics-tab-pane-inner">{renderStockRows(displayRows)}</div>
      ),
    },
    {
      key: "concept",
      label: "概念",
      children: (
        <div className="tactics-tab-pane-inner">
          <div className="tactics-concept-bar">
            <ConceptDropSelect
              conceptsCatalog={conceptConceptDtos}
              conceptCode={tactikConceptCode}
              placeholder="选择概念后查看成分与分数"
              onSelect={(c) => {
                setTactikConceptCode(c.code);
              }}
              onCleared={() => {
                setTactikConceptCode("");
                setConceptRows([]);
              }}
            />
          </div>
          {tactikConceptCode.trim() ? renderStockRows(displayRows) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="tactics-root" style={tacticsPageCssVars()}>
      <div className="tactics-main-split">
        <div className="tactics-col-left">
          {strategyLoading ? (
            <Spin className="tactics-strategy-loading" />
          ) : (
            <Select
              variant="borderless"
              popupMatchSelectWidth={false}
              className="tactics-strategy-select"
              popupClassName="tactics-strategy-select-popup"
              dropdownStyle={{
                ["--tactics-strategy-select-option-color" as string]:
                  TacticsPageUiConfig.strategySelect.optionTextColor,
              }}
              suffixIcon={<DownOutlined className="tactics-select-suffix-icon" />}
              value={strategyId || undefined}
              options={strategySelectOptions}
              placeholder="请选择策略"
              onChange={(v) => {
                const sid = String(v);
                setPickedFromSearch(false);
                setStrategyId(sid);
                void TacticsApi.recordStrategyVisit(spaceId, spaceProductId, sid);
              }}
            />
          )}
          <div
            className="tactics-stock-search-bar"
            style={{ width: tacticsWidthCss(TacticsPageUiConfig.stockSearch.width) }}
          >
            <SearchStockWithCatalog
              stockCode={searchStockCode || undefined}
              placeholder="输入股票名称或代码"
              borderRadius={6} 
              backgroundColor={TacticsPageUiConfig.stockSearch.backgroundColor}
              focusBackgroundColor={TacticsPageUiConfig.stockSearch.focusBackgroundColor}
              onSelect={onSearchStockSelect}
              onCleared={() => {
                setPickedFromSearch(false);
                setSearchStockCode("");
                const listCode = listSelectedStockCode.trim();
                setPanelStockCode(listCode);
                if (listCode) {
                  requestStockDetailForLink(listCode);
                }
              }}
            />
          </div>
          <div className="tactics-left-tabs-wrap">
            <Spin spinning={listLoading}>
              <Tabs
                activeKey={leftTab}
                onChange={(k) => setLeftTab(k as typeof leftTab)}
                className="tactics-left-tabs"
                indicator={leftTabsIndicator}
                items={leftTabsItems}
              />
            </Spin>
          </div>
        </div>
        <div className="tactics-col-right">
          <div className="tactics-right-head">
            <div className="tactics-stock-head-line">
              {panelStockCode.trim() ? (
                <div className="tactics-stock-title-row">
                  <StockLink
                    stockCode={panelStockCode.trim()}
                    stockName={selectedStockName || panelStockCode.trim()}
                    stockDetail={
                      stockLinkDetail.code === panelStockCode.trim()
                        ? stockLinkDetail.data
                        : null
                    }
                    detailLoading={
                      stockLinkDetail.code === panelStockCode.trim()
                        ? stockLinkDetail.loading
                        : false
                    }
                    dayKlines={
                      stockLinkDetail.code === panelStockCode.trim()
                        ? stockLinkDetail.dayKlines
                        : null
                    }
                    klineLoading={
                      stockLinkDetail.code === panelStockCode.trim()
                        ? stockLinkDetail.klineLoading
                        : false
                    }
                    onRequestDetail={requestStockDetailForLink}
                    stockNotesTab={buildStockNotesTabProps(
                      panelStockCode.trim(),
                      selectedStockName || panelStockCode.trim()
                    )}
                  />
                  <span className="tactics-stock-code-muted">{panelStockCode.trim()}</span>
                  {selectedStockPrice ? (
                    <StockPrice
                      mode="one-line"
                      price={selectedStockPrice.price}
                      change={selectedStockPrice.change}
                    />
                  ) : null}
                </div>
              ) : (
                <span className="tactics-muted-note">请先选择一只股票</span>
              )}
            </div>
            <div className="tactics-signal-head">
              {signalSaved?.opportunityIconUrl || signalSaved?.riskIconUrl ? (
                <>
                  <span className="tactics-signal-inline">
                    {signalSaved.opportunityIconUrl ? (
                      <img
                        src={signalSaved.opportunityIconUrl}
                        className="tactics-signal-inline-icon"
                        alt=""
                      />
                    ) : null}
                    <span>机会</span>
                  </span>
                  <span className="tactics-signal-inline">
                    {signalSaved.riskIconUrl ? (
                      <img
                        src={signalSaved.riskIconUrl}
                        className="tactics-signal-inline-icon"
                        alt=""
                      />
                    ) : null}
                    <span>风险</span>
                  </span>
                  <Button
                    type="text"
                    icon={<SettingOutlined />}
                    aria-label="设置信号"
                    onClick={() => openSignalSettings()}
                  />
                </>
              ) : (
                <>
                  <span className="tactics-muted-note">您尚未设置信号</span>
                  <Button
                    type="text"
                    icon={<SettingOutlined />}
                    aria-label="设置信号"
                    onClick={() => openSignalSettings()}
                  />
                </>
              )}
            </div>
          </div>
          <div className="tactics-chart-section">
            <div className="tactics-chart-hint-bar">
              <span className="tactics-chart-hint-left">
                {signalSaved?.opportunityIconUrl || signalSaved?.riskIconUrl
                  ? "图表中超过阈值的技术信号基于您的自定义设置，仅供参考，不应作为交易决策的唯一依据。"
                  : "策略技术得分为机械式计算结果，仅供参考，严禁作为交易决策的唯一依据。"}
              </span>
              <span className="tactics-chart-hint-right">{klineScoreHintText}</span>
            </div>
            <div className="tactics-chart-wrap" ref={chartRef}>
              <Spin spinning={panelLoading}>
                {chartWidth > 0 &&
                chartHeight > 0 &&
                panelStockCode.trim() &&
                klineData.length > 0 ? (
                  <div className="tactics-kline-chart">
                    <KChart
                      enablePriceHint
                      key={chartKey}
                      width={chartWidth}
                      height={chartHeight}
                      klineData={klineData}
                      fontSize={8}
                      gridUnit="month"
                      upColor="#DE554F"
                      downColor="#51A679"
                      backgroundColor="#FFFFFF"
                      gridColor="#F3F3F3"
                      gridType="solid"
                      labelColor="#545454"
                      valueHintColor="#545454"
                      indicateLineColor="#999999"
                      indicatorBgColor="#333333"
                      indicatorColor="#FFFFFF"
                      verticalFlex={[13, 4]}
                      defaultValueLineColor="#427BF6"
                      kBarType={["solid", "hollow"]}
                      klineWidth={{
                        start: 5,
                        min: 2,
                        max: 16,
                        lineWidth: 1,
                        minGap: 0.1,
                        maxGap: 2,
                      }}
                      zoom={{
                        minScale: 0.6,
                        maxScale: 6,
                      }}
                      volumeBar={{
                        field: "volume",
                        height: 80,
                        upColor: "#DE554F",
                        downColor: "#51A679",
                      }}
                      markGap={5}
                      onSelect={onKlineSelect}
                      onDeselect={onKlineDeselect}
                    />
                  </div>
                ) : (
                  <div className="tactics-chart-empty">
                    {panelStockCode.trim()
                      ? "暂无 K 线数据"
                      : "请从左侧列表或搜索框选择股票"}
                  </div>
                )}
              </Spin>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="策略市场"
        open={marketOpen}
        onCancel={() => setMarketOpen(false)}
        footer={[
          <Button key="cancel" className={RECT_BTN} onClick={() => setMarketOpen(false)}>
            取消
          </Button>,
          <Button
            key="ok"
            type="primary"
            className={RECT_BTN}
            onClick={confirmMarketPick}
          >
            确认切换
          </Button>,
        ]}
        width={960}
        className="tactics-market-modal"
        destroyOnHidden
      >
        <div className="tactics-market-grid">
          {marketStrategies.map((s) => {
            const hover = marketHover === s.id;
            const picked = marketPick === s.id;
            return (
              <button
                key={s.id}
                type="button"
                className={`tactics-market-card${hover ? " tactics-market-card--hover" : ""}${
                  picked ? " tactics-market-card--picked" : ""
                }`}
                onMouseEnter={() => setMarketHover(s.id)}
                onMouseLeave={() => setMarketHover("")}
                onClick={() => setMarketPick(s.id)}
              >
                <div className="tactics-market-card-rate">
                  回测收益 {Number(s.backtestReturnRate ?? 0).toFixed(2)}%
                  <Tooltip title="基于最近一年的回测数据">
                    <QuestionCircleOutlined className="tactics-q-icon" />
                  </Tooltip>
                </div>
                <div className="tactics-market-card-name">{s.name}</div>
                <div className="tactics-market-card-intro">{s.intro}</div>
              </button>
            );
          })}
        </div>
      </Modal>

      <Modal
        title="信号设置"
        open={signalSettingsOpen}
        onCancel={() => setSignalSettingsOpen(false)}
        footer={[
          <Button key="cancel" className={RECT_BTN} onClick={() => setSignalSettingsOpen(false)}>
            取消
          </Button>,
          <Button
            key="ok"
            type="primary"
            className={RECT_BTN}
            loading={signalBusy}
            onClick={() => void persistSignalSettings()}
          >
            保存
          </Button>,
        ]}
        width={560}
        className="tactics-signal-modal"
        destroyOnHidden
      >
        <Spin spinning={signalBusy}>
          <div className="tactics-signal-field">
            <span>
              机会出现阈值设置
              <Tooltip title="设置阈值后，超过此阈值的日线上方会出现图形标记，更加形象化。">
                <QuestionCircleOutlined className="tactics-q-icon" />
              </Tooltip>
            </span>
            <InputNumber
              min={0}
              max={100}
              className="tactics-input-flat"
              value={draftOppThr ?? undefined}
              onChange={(v) => setDraftOppThr(typeof v === "number" ? v : null)}
            />
          </div>
          <div className="tactics-signal-field">
            <span>机会信号图标</span>
            <div className="tactics-icon-pick-grid">
              {signalOpportunityOpts.map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  className={
                    draftOppIcon === opt.imageUrl
                      ? "tactics-icon-pick tactics-icon-pick--on"
                      : "tactics-icon-pick"
                  }
                  onClick={() => setDraftOppIcon(opt.imageUrl)}
                >
                  <img src={opt.imageUrl} alt="" />
                </button>
              ))}
            </div>
          </div>
          <div className="tactics-signal-field">
            <span>
              风险出现阈值设置
              <Tooltip title="设置阈值后，超过此阈值的日线上方会出现图形标记，更加形象化。">
                <QuestionCircleOutlined className="tactics-q-icon" />
              </Tooltip>
            </span>
            <InputNumber
              min={0}
              max={100}
              className="tactics-input-flat"
              value={draftRiskThr ?? undefined}
              onChange={(v) => setDraftRiskThr(typeof v === "number" ? v : null)}
            />
          </div>
          <div className="tactics-signal-field">
            <span>风险信号图标</span>
            <div className="tactics-icon-pick-grid">
              {signalRiskOpts.map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  className={
                    draftRiskIcon === opt.imageUrl
                      ? "tactics-icon-pick tactics-icon-pick--on"
                      : "tactics-icon-pick"
                  }
                  onClick={() => setDraftRiskIcon(opt.imageUrl)}
                >
                  <img src={opt.imageUrl} alt="" />
                </button>
              ))}
            </div>
          </div>
        </Spin>
      </Modal>
    </div>
  );
};

export default observer(TacticsPage);

interface RowProps {
  row: TacticsStockScoreRowDTO;
  active: boolean;
  stockLinkDetail: StockLinkDetailState;
  onPick: () => void;
  onRequestDetail: (code: string) => void;
  buildStockNotesTabProps: (stockCode: string, stockName?: string) => StockNotesTabProps;
}

function tacticStockRowJsx(p: RowProps): JSX.Element {
  const code = stockRowKey(p.row);
  return (
    <div
      key={code}
      role="button"
      tabIndex={0}
      className={
        p.active ? "tactics-stock-row tactics-stock-row--active" : "tactics-stock-row"
      }
      onClick={p.onPick}
      onKeyDown={(ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          p.onPick();
        }
      }}
    >
      <div className="tactics-stock-row-left">
        <StockLink
          stockCode={code}
          stockName={stockRowDisplayName(p.row)}
          stockDetail={p.stockLinkDetail.code === code ? p.stockLinkDetail.data : null}
          detailLoading={p.stockLinkDetail.code === code ? p.stockLinkDetail.loading : false}
          dayKlines={p.stockLinkDetail.code === code ? p.stockLinkDetail.dayKlines : null}
          klineLoading={p.stockLinkDetail.code === code ? p.stockLinkDetail.klineLoading : false}
          onRequestDetail={p.onRequestDetail}
          stockNotesTab={p.buildStockNotesTabProps(code, stockRowDisplayName(p.row))}
        />
        <StockPrice
          mode="one-line"
          price={p.row.lastPrice}
          change={ratioToStockPricePct(p.row.pctChange)}
          fontSize={TacticsPageUiConfig.stockListRow.stockPriceFontSize}
        />
      </div>
      <span className="tactics-stock-score">{formatTacticsScoreValue(p.row.score)}</span>
    </div>
  );
}
